import { Info, X } from "@phosphor-icons/react";
import { useEffect, useRef } from "react";

import { SETUP_STEPS } from "./copy";

export function SetupModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    closeButtonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, open]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="bg-primary/55 fixed inset-0 z-50 flex items-end justify-center p-0 backdrop-blur-sm sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="setup-title"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) {
          onClose();
        }
      }}
    >
      <div className="max-h-[92vh] w-full max-w-[760px] overflow-y-auto rounded-t-[28px] bg-white shadow-2xl sm:rounded-[28px]">
        <div className="border-border-subtle sticky top-0 z-10 flex items-center justify-between border-b bg-white/95 px-6 py-5 backdrop-blur sm:px-8">
          <div>
            <p className="text-muted text-[11px] tracking-[0.1em] uppercase">Behind the scenes</p>
            <h2 id="setup-title" className="text-primary mt-1 text-[24px] leading-8">
              How we prepare your connectors
            </h2>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="text-primary hover:bg-surface-subtle focus-visible:outline-ring flex size-10 items-center justify-center rounded-full transition focus-visible:outline-2 focus-visible:outline-offset-2"
            aria-label="Close setup details"
          >
            <X className="size-5" />
          </button>
        </div>

        <div className="px-6 py-7 sm:px-8 sm:py-8">
          <p className="text-muted max-w-[610px] text-[16px] leading-6">
            The finicky part happens on our preparation bench, twice. Once for heating and once for
            ventilation. It takes around 20 to 30 minutes per connector, which is why we do it for
            you.
          </p>

          <ol className="mt-8 space-y-0">
            {SETUP_STEPS.map((step, index) => (
              <li
                key={step.label}
                className="relative grid grid-cols-[42px_1fr] gap-4 pb-7 last:pb-0"
              >
                {index < SETUP_STEPS.length - 1 && (
                  <span
                    className="bg-border absolute top-10 bottom-0 left-[20px] w-px"
                    aria-hidden
                  />
                )}
                <span className="bg-daylight-subtle text-accent relative z-[1] flex size-[42px] items-center justify-center rounded-full text-[14px]">
                  {index + 1}
                </span>
                <div className="pt-1.5">
                  <h3 className="text-primary text-[17px] leading-6">{step.label}</h3>
                  <p className="text-muted mt-1 text-[14px] leading-5">{step.detail}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="bg-sky-subtle mt-8 rounded-2xl p-5 sm:flex sm:items-start sm:gap-4">
            <Info className="text-sky mb-3 size-6 shrink-0 sm:mb-0" weight="fill" aria-hidden />
            <div>
              <h3 className="text-primary text-[15px] leading-5">
                Your password is never shown in our setup guide
              </h3>
              <p className="text-muted mt-1 text-[13px] leading-5">
                This view recreates the technical process with safe example values. The connector
                stores the network details in its own memory so it can reconnect at your home.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
